import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Sparkles, Bot, Loader2, ArrowRight, Globe, Mail, Building2, CheckCircle2, Copy, ExternalLink, Clock } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "./LandingPage";

// Backend status → user-facing stage index (1:1 alignment with the steps).
const STATUS_TO_STEP_IDX = {
  queued:     0,
  crawling:   0,
  extracting: 1,
  embedding:  2,
  generating: 3,
  ready:      3,
  failed:     0,
  expired:    0,
};

const STEPS = [
  "Analyzing your website…",
  "Extracting business knowledge…",
  "Indexing for retrieval…",
  "Building your AI Sales Agent…",
];

// Maps a backend error (HTTP status + detail / job.error) → a clean UI variant.
// Returns { kind, title, body, retryable }.
function classifyDemoError({ httpStatus, detail, jobError } = {}) {
  const raw = (detail || jobError || "").toString();
  const lower = raw.toLowerCase();

  // Rate limit — most specific. Backend returns 429 with "You've created N demos in the last hour".
  if (httpStatus === 429 || /demos in the last hour|rate.?limit/i.test(raw)) {
    return {
      kind: "rate_limit",
      title: "You've reached the demo limit for this hour",
      body: "Please wait a bit before creating another demo, or contact us to set up a real workspace.",
      retryable: false,
    };
  }
  // Invalid URL — backend 400 patterns from validate_import_url / parsed.hostname check.
  if (
    httpStatus === 400 &&
    (/valid website url|incomplete|private or internal|determine the website|please enter a valid/i.test(raw))
  ) {
    return {
      kind: "invalid_url",
      title: "That URL doesn't look right",
      body: raw || "Please enter a valid public website URL (e.g. https://yourbusiness.com).",
      retryable: true,
    };
  }
  // Weak / limited site — surfaced both by backend 400 and by the soft job.error="low_content_fallback".
  if (/limited website information|low.?content|low.?content_fallback|could only access limited/i.test(lower)) {
    return {
      kind: "weak_site",
      title: "We could only access limited website information",
      body: "The site may block automated readers, require JavaScript, or be temporarily unreachable. We can still generate your AI assistant from inferred business context — try again, upload a PDF/CSV, or contact us.",
      retryable: true,
    };
  }
  // Timeout — hard 2-min cap on the polling loop.
  if (/longer than expected|timed out|timeout/i.test(lower)) {
    return {
      kind: "timeout",
      title: "Building is taking longer than expected",
      body: "Some sites are slow to crawl. Please try again — it usually finishes within a minute.",
      retryable: true,
    };
  }
  // Generic crawl/build failure — fallback.
  return {
    kind: "crawl_failed",
    title: "We couldn't finish building your demo",
    body: raw || "Something went wrong. Try a different URL or upload a PDF/CSV instead.",
    retryable: true,
  };
}

export default function DemoCreatePage() {
  const [form, setForm] = useState({ business_name: "", website_url: "", email: "" });
  const [stage, setStage] = useState("form"); // form | loading | success | error
  const [stepIdx, setStepIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stageLabel, setStageLabel] = useState("Preparing…");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null); // { kind, title, body, retryable }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.website_url.trim()) {
      toast.error("Business name and website URL are required");
      return;
    }
    setStage("loading");
    setStepIdx(0);
    setProgress(5);
    setStageLabel("Queued — preparing to start");
    setError(null);

    let jobId = null;
    try {
      // 1) Kick off the async job — returns immediately.
      const { data } = await api.post("/demo/jobs", {
        business_name: form.business_name.trim(),
        website_url: form.website_url.trim(),
        email: form.email.trim() || undefined,
      });
      jobId = data.job_id;
      setProgress(data.progress || 5);
      setStageLabel(data.stage_label || "Queued…");
    } catch (err) {
      const classified = classifyDemoError({
        httpStatus: err.response?.status,
        detail: err.response?.data?.detail,
      });
      setError(classified);
      setStage("error");
      return;
    }

    // 2) Poll for status. 1.5s interval, 2-minute hard cap.
    const startedAt = Date.now();
    const HARD_CAP_MS = 120 * 1000;
    const interval = setInterval(async () => {
      try {
        const { data: job } = await api.get(`/demo/jobs/${jobId}`);
        setProgress(job.progress || 0);
        setStageLabel(job.stage_label || job.status);
        const idx = STATUS_TO_STEP_IDX[job.status] ?? 0;
        setStepIdx(idx);

        if (job.status === "ready") {
          clearInterval(interval);
          setResult({
            chatbot_url: job.demo_url || `/chat/${job.slug}`,
            slug: job.slug,
            expires_at: job.expires_at,
            reused: job.reused,
            stats: job.stats,
            // Resilience v1 — soft marker when the crawl fell back to inferred FAQs.
            low_content_fallback: job.error === "low_content_fallback" || !!job.stats?.low_content_fallback,
          });
          setStage("success");
          toast.success(job.reused ? "Reusing your recent demo" : "Your AI assistant is ready!");
        } else if (job.status === "failed") {
          clearInterval(interval);
          setError(classifyDemoError({ jobError: job.error }));
          setStage("error");
        } else if (Date.now() - startedAt > HARD_CAP_MS) {
          clearInterval(interval);
          setError(classifyDemoError({ detail: "longer than expected" }));
          setStage("error");
        }
      } catch (err) {
        // Transient poll failure — keep going.
      }
    }, 1500);
  };

  const reset = () => { setStage("form"); setResult(null); setError(null); setProgress(0); };
  const chatUrl = result ? `${window.location.origin}${result.chatbot_url}` : "";
  const expiresAt = result?.expires_at ? new Date(result.expires_at) : null;

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="text-center mb-10">
          <Badge className="bg-accent text-accent-foreground mb-4">Try it free · No signup</Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mb-4 text-balance" data-testid="demo-title">
            Your AI Sales Assistant — in 60 seconds
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
            Drop your website URL and we'll train a chatbot on it instantly. No credit card. No account.
          </p>
        </div>

        {stage === "form" && (
          <Card className="p-6 sm:p-8" data-testid="demo-form-card">
            <div className="mb-5 p-4 rounded-lg bg-muted/30 border border-dashed text-sm" data-testid="demo-explainer">
              <p className="font-medium text-foreground mb-2">Rudras-AI will analyze your business website and generate a live AI Sales Assistant tailored for your business.</p>
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-muted-foreground leading-relaxed">
                <li>· Answers customer questions</li>
                <li>· Qualifies and captures leads</li>
                <li>· Handles your FAQs automatically</li>
                <li>· Works 24/7</li>
                <li>· Supports voice + chat</li>
                <li>· Ready in under a minute</li>
              </ul>
            </div>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="flex items-center gap-2"><Building2 className="w-4 h-4 text-accent"/>Business name</Label>
                <Input
                  data-testid="demo-business-name"
                  required
                  className="mt-2"
                  placeholder="e.g. Rudra's Café"
                  value={form.business_name}
                  onChange={e => setForm({ ...form, business_name: e.target.value })}
                  autoFocus
                />
              </div>
              <div>
                <Label className="flex items-center gap-2"><Globe className="w-4 h-4 text-accent"/>Website URL</Label>
                <Input
                  data-testid="demo-website-url"
                  required
                  className="mt-2"
                  placeholder="https://yourbusiness.com"
                  value={form.website_url}
                  onChange={e => setForm({ ...form, website_url: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1.5">We'll crawl up to 30 pages — typically takes 20–60 seconds.</p>
              </div>
              <div>
                <Label className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground"/>Email <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  data-testid="demo-email"
                  type="email"
                  className="mt-2"
                  placeholder="you@business.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <Button type="submit" data-testid="demo-submit" size="lg" className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-base">
                Create My Demo Assistant <ArrowRight className="ml-2 w-4 h-4"/>
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Demo expires in 48 hours · 100 message limit · Powered by Rudras-AI
              </p>
            </form>
          </Card>
        )}

        {stage === "loading" && (
          <Card className="p-8 sm:p-12 text-center" data-testid="demo-loading">
            <div className="w-16 h-16 mx-auto rounded-full bg-accent/15 grid place-items-center mb-6">
              <Loader2 className="w-8 h-8 text-accent animate-spin"/>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-2">Building your assistant…</h2>
            <p className="text-muted-foreground mb-6" data-testid="demo-stage-label">{stageLabel}</p>

            {/* Live progress bar driven by backend polling. */}
            <div className="max-w-sm mx-auto mb-8">
              <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                <div
                  className="h-full bg-accent transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                  data-testid="demo-progress-bar"
                />
              </div>
              <div className="text-xs text-muted-foreground mt-2" data-testid="demo-progress-text">
                {progress}% — keep this tab open
              </div>
            </div>

            <div className="space-y-3 max-w-sm mx-auto text-left">
              {STEPS.map((step, i) => (
                <div key={i} className={`flex items-center gap-3 text-sm transition-opacity ${i <= stepIdx ? "opacity-100" : "opacity-30"}`}>
                  {i < stepIdx ? <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0"/> :
                   i === stepIdx ? <Loader2 className="w-4 h-4 animate-spin text-accent flex-shrink-0"/> :
                   <div className="w-4 h-4 rounded-full border-2 border-muted flex-shrink-0"/>}
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {stage === "success" && result && (
          <Card className="p-6 sm:p-10 text-center" data-testid="demo-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-accent grid place-items-center mb-6">
              <Sparkles className="w-8 h-8 text-accent-foreground"/>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-3">Your AI Sales Assistant is ready.</h2>
            <p className="text-muted-foreground mb-6">Test it the way a real customer would — ask about your services, pricing, or how to get in touch.</p>

            {result.low_content_fallback && (
              <div
                className="mb-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 text-left"
                data-testid="demo-weak-site-notice"
              >
                <span className="font-medium">Heads up:</span> we could only access limited information from your
                website, so we generated this assistant from inferred business context. Try opening the chat — you can
                always re-run with a different URL or upload PDFs later.
              </div>
            )}

            <div className="bg-secondary rounded-xl p-4 mb-6 flex items-center gap-2 text-sm font-mono break-all">
              <Bot className="w-4 h-4 text-accent flex-shrink-0"/>
              <span className="flex-1 text-left" data-testid="demo-chat-url">{chatUrl}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(chatUrl); toast.success("Copied"); }}
                className="p-1.5 hover:bg-card rounded transition flex-shrink-0"
                aria-label="Copy"
                data-testid="demo-copy-url"
              >
                <Copy className="w-4 h-4"/>
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={result.chatbot_url} target="_blank" rel="noreferrer" className="flex-1 sm:flex-initial">
                <Button size="lg" data-testid="demo-open-chat" className="w-full rounded-full bg-primary hover:bg-primary/90 h-12">
                  Open my chatbot <ExternalLink className="ml-2 w-4 h-4"/>
                </Button>
              </a>
              <Button size="lg" variant="outline" onClick={reset} className="rounded-full h-12">
                Create another
              </Button>
            </div>

            {expiresAt && (
              <p className="text-xs text-muted-foreground mt-6 flex items-center justify-center gap-1">
                <Clock className="w-3 h-3"/> Demo expires {expiresAt.toLocaleString()}
              </p>
            )}
            <div className="mt-8 pt-6 border-t" data-testid="demo-next-steps">
              <p className="text-sm font-medium mb-3">Try these next</p>
              <ul className="text-sm text-muted-foreground space-y-1.5 mb-5 max-w-md mx-auto text-left">
                <li>· Ask your bot something a new customer would ask</li>
                <li>· Send the link to a teammate or a friend to test</li>
                <li>· When you're ready, claim this assistant on your own account</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Link to="/register">
                  <Button variant="outline" className="rounded-full" data-testid="demo-claim-cta">Claim this assistant →</Button>
                </Link>
                <a href="mailto:hello@rudras-ai.com?subject=Booking%20a%20Rudras-AI%20demo" className="inline-block">
                  <Button variant="ghost" className="rounded-full">Book a setup call</Button>
                </a>
              </div>
            </div>
          </Card>
        )}

        {stage === "error" && error && (
          <Card className="p-6 sm:p-10 text-center" data-testid="demo-error">
            <div className={`w-16 h-16 mx-auto rounded-full grid place-items-center mb-6 ${
              error.kind === "rate_limit" ? "bg-amber-100" :
              error.kind === "weak_site"  ? "bg-amber-100" :
              error.kind === "invalid_url"? "bg-blue-100"  :
              error.kind === "timeout"    ? "bg-slate-100" :
              "bg-destructive/15"
            }`}>
              {error.kind === "rate_limit" ? <Clock className="w-8 h-8 text-amber-600"/> :
               error.kind === "weak_site"  ? <Globe className="w-8 h-8 text-amber-600"/> :
               error.kind === "invalid_url"? <Globe className="w-8 h-8 text-blue-600"/>  :
               error.kind === "timeout"    ? <Loader2 className="w-8 h-8 text-slate-600"/> :
               <Globe className="w-8 h-8 text-destructive"/>}
            </div>
            <h2 className="font-display text-2xl font-semibold mb-3" data-testid={`demo-error-title-${error.kind}`}>{error.title}</h2>
            <p className="text-muted-foreground mb-6" data-testid="demo-error-body">{error.body}</p>
            {error.retryable && (
              <Button onClick={reset} className="rounded-full bg-primary hover:bg-primary/90" data-testid="demo-error-retry">Try again</Button>
            )}
            {!error.retryable && (
              <Button onClick={reset} variant="outline" className="rounded-full" data-testid="demo-error-back">Back to start</Button>
            )}
          </Card>
        )}
      </section>
    </PageShell>
  );
}
